import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { User, CreditCard, Eye } from "lucide-react"
import { getMember, updateMember } from "../../services/memberService"
import { fr } from "../../locales/fr"
import { PaymentModal } from "../../components/members/PaymentModal"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import { Label } from "../../components/ui/Label"

const textareaClass = `
  flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
  focus-visible:ring-offset-2 focus-visible:ring-offset-background
`

export default function MemberEdit() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [payMessage, setPayMessage] = useState("")

  const d = fr.memberDetail

  useEffect(() => {
    const load = async () => {
      const data = await getMember(id)
      setForm(data.member)
      setLoading(false)
    }
    load()
  }, [id])

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    })
  }

  const save = async () => {
    setSaving(true)
    try {
      await updateMember(id, form)
      navigate(`/members/${id}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !form) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        {fr.common.loading}
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">{d.editTitle}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {d.memberNumberLabel} : {form.memberNumber}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(`/members/${id}`)}>
            <Eye className="h-4 w-4" />
            {d.viewProfile}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => navigate("/members")}>
            {d.backToList}
          </Button>
        </div>
      </div>

      {payMessage && (
        <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          {payMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            <CardTitle>{d.identitySection}</CardTitle>
          </div>
          <CardDescription>{d.infoDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">{d.firstName}</Label>
              <Input
                id="firstName"
                name="firstName"
                value={form.firstName || ""}
                onChange={handleChange}
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">{d.lastName}</Label>
              <Input
                id="lastName"
                name="lastName"
                value={form.lastName || ""}
                onChange={handleChange}
                autoComplete="family-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{d.phone}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone || ""}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{d.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={form.email || ""}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cin">{d.cin}</Label>
              <Input id="cin" name="cin" value={form.cin || ""} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">{d.gender}</Label>
              <Select
                id="gender"
                value={form.gender || ""}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
              >
                <option value="">{fr.gender.label}</option>
                <option value="male">{fr.gender.male}</option>
                <option value="female">{fr.gender.female}</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="birthDate">{d.birthDate}</Label>
              <Input
                id="birthDate"
                name="birthDate"
                type="date"
                value={form.birthDate || ""}
                onChange={handleChange}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="profession">{d.profession}</Label>
              <Input
                id="profession"
                name="profession"
                value={form.profession || ""}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">{d.address}</Label>
            <textarea
              id="address"
              name="address"
              value={form.address || ""}
              onChange={handleChange}
              rows={4}
              className={textareaClass}
            />
          </div>

          <div className="flex flex-wrap gap-3 border-t border-border pt-6">
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? fr.common.saving : fr.common.save}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(`/members/${id}`)}>
              {fr.common.cancel}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-2">
              <CreditCard className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <CardTitle>{d.paymentCardTitle}</CardTitle>
                <CardDescription>{d.paymentCardHint}</CardDescription>
              </div>
            </div>
            <Button type="button" variant="default" size="sm" onClick={() => setPaymentOpen(true)}>
              <CreditCard className="h-4 w-4" />
              {d.paymentOpen}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        memberId={Number(id)}
        onSuccess={() => setPayMessage(d.paymentSuccess)}
      />
    </div>
  )
}
